/**
 * Motion spec — cara template video bergerak TERUS dari awal sampai akhir,
 * bukan slideshow adegan yang berdiri sendiri.
 *
 * Model: satu PANGGUNG (`stage`, HTML template yang hidup sepanjang video)
 * plus lapisan BEAT (HTML adegan lama, satu per kalimat narasi) di atasnya.
 *
 * - `states`: keadaan elemen panggung (`data-el="nama"`) per beat. Beat ke-i
 *   memakai `states[i % n]`, jadi template tidak bergantung pada jumlah beat.
 *   Di setiap batas beat elemen berpindah MULUS ke keadaan berikutnya
 *   (`morph`) — inilah yang membuat video nyambung, bukan potong-potong.
 *   Satu keadaan menggambarkan elemen secara utuh: properti angka yang tidak
 *   disebut kembali ke nilai netralnya (x 0, scale 1, opacity 1, …).
 * - `loops`: gerak latar yang jalan sepanjang video (float, sway, spin, …).
 * - `camera`: panggung didorong/digeser pelan sepanjang durasi.
 * - `beatIn`/`beatOut`: cara lapisan beat masuk dan keluar. `beatOut` selain
 *   `none` membuat beat lama keluar SAMBIL beat baru masuk (tumpang tindih).
 *
 * Ini DATA, bukan kode (aturan #18/#19): AI tidak pernah menulis script.
 * `validateMotion` menolak (bukan membersihkan) properti, easing, preset, dan
 * angka di luar daftar; script penafsirnya hanya ada di composition.ts.
 * Template tanpa motion spec dan tanpa panggung berperilaku persis seperti
 * template adegan lama (fade-in 0,35 dtk, tanpa keluar).
 */

export const EASES = [
  'none',
  'power1.in',
  'power1.out',
  'power1.inOut',
  'power2.in',
  'power2.out',
  'power2.inOut',
  'power3.in',
  'power3.out',
  'power3.inOut',
  'power4.out',
  'power4.inOut',
  'expo.out',
  'expo.inOut',
  'sine.inOut',
  'circ.inOut',
  'back.out',
  'elastic.out',
] as const
export type Ease = (typeof EASES)[number]

export const ENTER_PRESETS = ['fade', 'up', 'down', 'left', 'right', 'scale', 'blur', 'wipe-up', 'wipe-right', 'none'] as const
export type EnterPreset = (typeof ENTER_PRESETS)[number]
export const EXIT_PRESETS = ['fade', 'up', 'down', 'left', 'right', 'scale', 'blur', 'none'] as const
export type ExitPreset = (typeof EXIT_PRESETS)[number]
export const LOOP_PRESETS = ['float', 'drift', 'sway', 'pulse', 'spin', 'shimmer', 'progress'] as const
export type LoopPreset = (typeof LOOP_PRESETS)[number]
export const CAMERA_PRESETS = ['none', 'push-in', 'pull-out', 'pan-up', 'pan-down', 'pan-left', 'pan-right'] as const
export type CameraPreset = (typeof CAMERA_PRESETS)[number]

/** Batas angka per properti. x/y/blur dalam piksel pada lebar 1080. */
const ANGKA = {
  x: [-4000, 4000, 0],
  y: [-4000, 4000, 0],
  scale: [0, 6, 1],
  scaleX: [0, 6, 1],
  scaleY: [0, 6, 1],
  rotate: [-1080, 1080, 0],
  skewX: [-60, 60, 0],
  skewY: [-60, 60, 0],
  opacity: [0, 1, 1],
  blur: [0, 80, 0],
} as const satisfies Record<string, readonly [number, number, number]>
type PropAngka = keyof typeof ANGKA
const WARNA = ['color', 'background'] as const
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i

export interface ElState {
  x?: number
  y?: number
  scale?: number
  scaleX?: number
  scaleY?: number
  rotate?: number
  skewX?: number
  skewY?: number
  opacity?: number
  blur?: number
  color?: string
  background?: string
  /** clip-path inset dalam persen: [atas, kanan, bawah, kiri]. */
  clip?: [number, number, number, number]
}

export type StageState = Record<string, ElState>

export interface MotionSpec {
  states?: StageState[]
  /** Keadaan awal di detik 0; tanpa ini video dibuka langsung pada states[0]. */
  first?: StageState
  /** Pengganti keadaan beat terakhir (penutup), kalau beat lebih dari satu. */
  last?: StageState
  morph?: { duration?: number; ease?: Ease; lead?: number }
  loops?: { el: string; preset: LoopPreset; amount?: number; period?: number }[]
  camera?: { preset: CameraPreset; amount?: number }
  beatIn?: { preset: EnterPreset; duration?: number; ease?: Ease }
  beatOut?: { preset: ExitPreset; duration?: number; ease?: Ease }
  /** Jeda antarelemen `data-enter` di dalam beat. */
  stagger?: number
}

export const EL_NAME = /^[a-z][a-z0-9-]{0,30}$/
const MAX_STATES = 12
const MAX_ELS = 24
const MAX_LOOPS = 16

/** Nama-nama `data-el` di HTML panggung. Nilai yang bukan literal ditolak validator. */
export function elementNames(html: string): { names: Set<string>; errors: string[] } {
  const names = new Set<string>()
  const errors: string[] = []
  for (const m of html.matchAll(/\sdata-el\s*=\s*["']?([^"'\s>]*)/gi)) {
    const n = m[1]!
    if (!EL_NAME.test(n)) errors.push(`data-el="${n}" must be a plain name like "blob" or "title-bar" (lowercase letters, digits, dashes)`)
    else names.add(n)
  }
  return { names, errors }
}

function angka(v: unknown, min: number, max: number, path: string, errors: string[]): number | undefined {
  if (v === undefined) return undefined
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    errors.push(`${path} must be a number`)
    return undefined
  }
  if (v < min || v > max) {
    errors.push(`${path} must be between ${min} and ${max}`)
    return undefined
  }
  return Math.round(v * 1000) / 1000
}

function pilihan<T extends string>(v: unknown, daftar: readonly T[], path: string, errors: string[]): T | undefined {
  if (v === undefined) return undefined
  if (typeof v === 'string' && (daftar as readonly string[]).includes(v)) return v as T
  errors.push(`${path} must be one of ${daftar.join(', ')}`)
  return undefined
}

function objek(v: unknown, path: string, errors: string[]): Record<string, unknown> | null {
  if (v === undefined) return null
  if (!v || typeof v !== 'object' || Array.isArray(v)) {
    errors.push(`${path} must be an object`)
    return null
  }
  return v as Record<string, unknown>
}

function bacaElState(raw: unknown, path: string, errors: string[]): ElState | null {
  const r = objek(raw, path, errors)
  if (!r) return null
  const out: ElState = {}
  for (const [k, v] of Object.entries(r)) {
    if (k in ANGKA) {
      const [min, max] = ANGKA[k as PropAngka]
      const n = angka(v, min, max, `${path}.${k}`, errors)
      if (n !== undefined) out[k as PropAngka] = n
    } else if ((WARNA as readonly string[]).includes(k)) {
      if (typeof v === 'string' && HEX.test(v)) out[k as (typeof WARNA)[number]] = v
      else errors.push(`${path}.${k} must be a hex colour like #1a1a1a`)
    } else if (k === 'clip') {
      if (Array.isArray(v) && v.length === 4 && v.every((x) => typeof x === 'number' && x >= 0 && x <= 100)) {
        out.clip = v.map((x) => Math.round(x * 100) / 100) as ElState['clip']
      } else errors.push(`${path}.clip must be [top, right, bottom, left] percentages from 0 to 100`)
    } else {
      errors.push(`${path}.${k} is not an animatable property — use ${[...Object.keys(ANGKA), ...WARNA, 'clip'].join(', ')}`)
    }
  }
  return out
}

function bacaStageState(raw: unknown, path: string, els: Set<string>, errors: string[]): StageState | null {
  const r = objek(raw, path, errors)
  if (!r) return null
  const out: StageState = {}
  const entri = Object.entries(r)
  if (entri.length > MAX_ELS) errors.push(`${path} animates more than ${MAX_ELS} elements`)
  for (const [el, v] of entri.slice(0, MAX_ELS)) {
    if (!els.has(el)) {
      errors.push(`${path}.${el}: there is no data-el="${el}" in stage`)
      continue
    }
    const s = bacaElState(v, `${path}.${el}`, errors)
    if (s) out[el] = s
  }
  return out
}

/**
 * Periksa motion spec dari AI terhadap nama `data-el` di panggung. Pesannya
 * untuk AI. Mengembalikan spec yang sudah dirapikan angkanya, atau null kalau
 * ada satu saja masalah.
 */
export function validateMotion(raw: unknown, els: Set<string>): { motion: MotionSpec | null; errors: string[] } {
  const errors: string[] = []
  const r = objek(raw, 'motion', errors)
  if (!r) return { motion: null, errors: errors.length ? errors : [] }
  const m: MotionSpec = {}
  const dikenal = ['states', 'first', 'last', 'morph', 'loops', 'camera', 'beatIn', 'beatOut', 'stagger']
  for (const k of Object.keys(r)) if (!dikenal.includes(k)) errors.push(`motion.${k} is not a known key — use ${dikenal.join(', ')}`)

  if (r.states !== undefined) {
    if (!Array.isArray(r.states) || !r.states.length || r.states.length > MAX_STATES) {
      errors.push(`motion.states must be an array of 1–${MAX_STATES} states`)
    } else {
      m.states = r.states.map((s, i) => bacaStageState(s, `motion.states[${i}]`, els, errors) ?? {})
    }
  }
  for (const k of ['first', 'last'] as const) {
    if (r[k] === undefined) continue
    if (!m.states) errors.push(`motion.${k} needs motion.states`)
    const s = bacaStageState(r[k], `motion.${k}`, els, errors)
    if (s) m[k] = s
  }

  const morph = objek(r.morph, 'motion.morph', errors)
  if (morph) {
    m.morph = {}
    const d = angka(morph.duration, 0.1, 4, 'motion.morph.duration', errors)
    const e = pilihan(morph.ease, EASES, 'motion.morph.ease', errors)
    const l = angka(morph.lead, 0, 2, 'motion.morph.lead', errors)
    if (d !== undefined) m.morph.duration = d
    if (e !== undefined) m.morph.ease = e
    if (l !== undefined) m.morph.lead = l
  }

  if (r.loops !== undefined) {
    if (!Array.isArray(r.loops) || r.loops.length > MAX_LOOPS) errors.push(`motion.loops must be an array of at most ${MAX_LOOPS}`)
    else {
      m.loops = []
      r.loops.forEach((raw, i) => {
        const l = objek(raw, `motion.loops[${i}]`, errors)
        if (!l) return
        const el = typeof l.el === 'string' ? l.el : ''
        if (!els.has(el)) errors.push(`motion.loops[${i}].el: there is no data-el="${el}" in stage`)
        const preset = pilihan(l.preset, LOOP_PRESETS, `motion.loops[${i}].preset`, errors)
        if (!preset) errors.push(`motion.loops[${i}].preset is required`)
        const amount = angka(l.amount, 0, 3, `motion.loops[${i}].amount`, errors)
        const period = angka(l.period, 1, 30, `motion.loops[${i}].period`, errors)
        if (els.has(el) && preset) m.loops!.push({ el, preset, ...(amount !== undefined ? { amount } : {}), ...(period !== undefined ? { period } : {}) })
      })
    }
  }

  const cam = objek(r.camera, 'motion.camera', errors)
  if (cam) {
    const preset = pilihan(cam.preset, CAMERA_PRESETS, 'motion.camera.preset', errors)
    const amount = angka(cam.amount, 0, 3, 'motion.camera.amount', errors)
    if (preset) m.camera = { preset, ...(amount !== undefined ? { amount } : {}) }
  }

  const bIn = objek(r.beatIn, 'motion.beatIn', errors)
  if (bIn) {
    const preset = pilihan(bIn.preset, ENTER_PRESETS, 'motion.beatIn.preset', errors)
    const duration = angka(bIn.duration, 0, 3, 'motion.beatIn.duration', errors)
    const ease = pilihan(bIn.ease, EASES, 'motion.beatIn.ease', errors)
    if (preset) m.beatIn = { preset, ...(duration !== undefined ? { duration } : {}), ...(ease ? { ease } : {}) }
  }
  const bOut = objek(r.beatOut, 'motion.beatOut', errors)
  if (bOut) {
    const preset = pilihan(bOut.preset, EXIT_PRESETS, 'motion.beatOut.preset', errors)
    const duration = angka(bOut.duration, 0, 3, 'motion.beatOut.duration', errors)
    const ease = pilihan(bOut.ease, EASES, 'motion.beatOut.ease', errors)
    if (preset) m.beatOut = { preset, ...(duration !== undefined ? { duration } : {}), ...(ease ? { ease } : {}) }
  }
  const stagger = angka(r.stagger, 0, 1, 'motion.stagger', errors)
  if (stagger !== undefined) m.stagger = stagger

  // Dua tween pada properti yang sama di satu elemen saling timpa tiap frame
  // (gambarnya bergetar). Loop dan keadaan karena itu tidak boleh berbagi
  // elemen: AI diminta membungkusnya dengan induk ber-data-el sendiri.
  const berkeadaan = new Set<string>()
  for (const s of [...(m.states ?? []), m.first ?? {}, m.last ?? {}]) for (const el of Object.keys(s)) berkeadaan.add(el)
  const dilup = new Set<string>()
  for (const [i, l] of (m.loops ?? []).entries()) {
    if (berkeadaan.has(l.el)) errors.push(`motion.loops[${i}]: "${l.el}" is already moved by states — wrap it in a parent with its own data-el and loop that instead`)
    if (dilup.has(l.el)) errors.push(`motion.loops[${i}]: "${l.el}" has more than one loop — wrap it in a parent with its own data-el`)
    dilup.add(l.el)
  }

  const uniq = [...new Set(errors)]
  return uniq.length ? { motion: null, errors: uniq } : { motion: m, errors: [] }
}

// ------------------------------------------------------------------ jadwal

export interface Beat {
  start: number
  duration: number
}

/** Satu langkah keadaan panggung: `dur` 0 = dipasang langsung. */
export interface StateKey {
  at: number
  dur: number
  ease: Ease
  from: StageState | null
  to: StageState
}

export interface LoopKey {
  el: string
  from: Record<string, number>
  to: Record<string, number>
  /** Setengah periode (satu arah), detik. */
  dur: number
  repeat: number
  yoyo: boolean
  ease: Ease
  origin?: string
}

export interface BeatKey {
  start: number
  /** Lama klip di komposisi, termasuk waktu keluar yang menumpuk beat berikutnya. */
  clip: number
  in: { preset: EnterPreset; duration: number; ease: Ease }
  out: { preset: ExitPreset; duration: number; ease: Ease; at: number } | null
}

export interface MotionPlan {
  keys: StateKey[]
  loops: LoopKey[]
  camera: { from: Record<string, number>; to: Record<string, number> } | null
  beats: BeatKey[]
  stagger: number
}

const r3 = (n: number) => Math.round(n * 1000) / 1000

/** Lengkapi keadaan: properti angka yang dipakai elemen di keadaan mana pun tapi tak disebut di sini kembali ke netral. */
function lengkapi(states: StageState[]): StageState[] {
  const dipakai: Record<string, Set<string>> = {}
  for (const s of states) for (const [el, p] of Object.entries(s)) for (const k of Object.keys(p)) (dipakai[el] ??= new Set()).add(k)
  return states.map((s) => {
    const out: StageState = {}
    for (const [el, props] of Object.entries(dipakai)) {
      const st: ElState = { ...(s[el] ?? {}) }
      for (const k of props) {
        if (k in st) continue
        if (k in ANGKA) st[k as PropAngka] = ANGKA[k as PropAngka][2]
        else if (k === 'clip') st.clip = [0, 0, 0, 0]
        // Warna yang tidak disebut: biarkan warna sebelumnya.
      }
      out[el] = st
    }
    return out
  })
}

/**
 * Turunkan jadwal animasi dari spec + waktu beat sungguhan (dari audio).
 * Semua angka di sini dalam detik dan piksel-1080; konversi ke piksel bingkai
 * dilakukan script penafsir.
 */
export function planMotion(spec: MotionSpec | undefined, beats: readonly Beat[]): MotionPlan {
  const m = spec ?? {}
  const total = beats.reduce((n, b) => Math.max(n, b.start + b.duration), 0)
  const n = beats.length

  // --- Keadaan panggung per beat.
  const keys: StateKey[] = []
  if (m.states?.length && n) {
    const urut: StageState[] = beats.map((_, i) => (i === n - 1 && n > 1 && m.last ? m.last : m.states![i % m.states!.length]!))
    const [awal, ...perBeat] = lengkapi(m.first ? [m.first, ...urut] : [urut[0]!, ...urut])
    const dur = m.morph?.duration ?? 0.8
    const ease = m.morph?.ease ?? 'power2.inOut'
    const lead = m.morph?.lead ?? 0.25
    keys.push({ at: 0, dur: 0, ease: 'none', from: null, to: awal! })
    let prev = awal!
    let prevAt = 0
    perBeat.forEach((st, i) => {
      // Beat pertama tanpa `first`: sudah dipasang di detik 0.
      if (i === 0 && !m.first) return
      const at = i === 0 ? 0 : Math.max(prevAt, beats[i]!.start - lead)
      // Jangan sampai menabrak perpindahan berikutnya: dua tween pada
      // properti yang sama saling timpa.
      const batas = i + 1 < n ? Math.max(0.1, beats[i + 1]!.start - lead - at - 0.02) : total - at
      keys.push({ at: r3(at), dur: r3(Math.max(0.05, Math.min(dur, batas))), ease, from: prev, to: st })
      prev = st
      prevAt = at + Math.min(dur, batas)
    })
  }

  // --- Loop sepanjang video.
  const loops: LoopKey[] = []
  for (const l of m.loops ?? []) {
    const a = l.amount ?? 1
    const p = l.period ?? 4
    const half = p / 2
    const ulang = (d: number) => Math.max(0, Math.ceil(total / d) - 1)
    switch (l.preset) {
      case 'float':
        loops.push({ el: l.el, from: { y: -14 * a }, to: { y: 14 * a }, dur: half, repeat: ulang(half), yoyo: true, ease: 'sine.inOut' })
        break
      case 'drift':
        loops.push({ el: l.el, from: { x: -24 * a, y: 10 * a }, to: { x: 24 * a, y: -10 * a }, dur: half, repeat: ulang(half), yoyo: true, ease: 'sine.inOut' })
        break
      case 'sway':
        loops.push({ el: l.el, from: { rotate: -4 * a }, to: { rotate: 4 * a }, dur: half, repeat: ulang(half), yoyo: true, ease: 'sine.inOut' })
        break
      case 'pulse':
        loops.push({ el: l.el, from: { scale: 1 }, to: { scale: 1 + 0.06 * a }, dur: half, repeat: ulang(half), yoyo: true, ease: 'sine.inOut' })
        break
      case 'shimmer':
        loops.push({ el: l.el, from: { opacity: 1 }, to: { opacity: Math.max(0, 1 - 0.4 * a) }, dur: half, repeat: ulang(half), yoyo: true, ease: 'sine.inOut' })
        break
      case 'spin':
        loops.push({ el: l.el, from: { rotate: 0 }, to: { rotate: 360 }, dur: p, repeat: ulang(p), yoyo: false, ease: 'none' })
        break
      case 'progress':
        loops.push({ el: l.el, from: { scaleX: 0 }, to: { scaleX: 1 }, dur: total, repeat: 0, yoyo: false, ease: 'none', origin: '0% 50%' })
        break
    }
  }

  // --- Kamera: skala selalu ≥1 supaya tepi panggung tidak pernah terlihat.
  let camera: MotionPlan['camera'] = null
  const cp = m.camera?.preset ?? 'none'
  if (cp !== 'none' && total > 0) {
    const a = m.camera?.amount ?? 1
    const s = 1 + 0.12 * a
    const geser = 540 * (s - 1) * 0.9
    camera =
      cp === 'push-in'
        ? { from: { scale: 1 }, to: { scale: s } }
        : cp === 'pull-out'
          ? { from: { scale: s }, to: { scale: 1 } }
          : cp === 'pan-up'
            ? { from: { scale: s, y: geser }, to: { scale: s, y: -geser } }
            : cp === 'pan-down'
              ? { from: { scale: s, y: -geser }, to: { scale: s, y: geser } }
              : cp === 'pan-left'
                ? { from: { scale: s, x: geser }, to: { scale: s, x: -geser } }
                : { from: { scale: s, x: -geser }, to: { scale: s, x: geser } }
  }

  // --- Lapisan beat.
  const bIn = { preset: m.beatIn?.preset ?? 'fade', duration: m.beatIn?.duration ?? 0.35, ease: m.beatIn?.ease ?? 'power1.out' }
  const out = m.beatOut?.preset && m.beatOut.preset !== 'none' ? { preset: m.beatOut.preset, duration: m.beatOut.duration ?? 0.4, ease: m.beatOut.ease ?? 'power2.in' } : null
  const beatKeys: BeatKey[] = beats.map((b, i) => {
    const terakhir = i === n - 1
    const keluar = out && !terakhir ? { ...out, duration: r3(Math.min(out.duration, beats[i + 1]!.duration)), at: r3(b.start + b.duration) } : null
    return { start: b.start, clip: r3(b.duration + (keluar?.duration ?? 0)), in: bIn, out: keluar }
  })

  return { keys, loops, camera, beats: beatKeys, stagger: m.stagger ?? 0.12 }
}

/** CSS statis sebuah keadaan — untuk preview per adegan (tanpa animasi). */
export function stateCss(state: StageState, u: number): string {
  return Object.entries(state)
    .map(([el, p]) => {
      const t: string[] = []
      if (p.x !== undefined || p.y !== undefined) t.push(`translate(${r3((p.x ?? 0) * u)}px,${r3((p.y ?? 0) * u)}px)`)
      if (p.rotate !== undefined) t.push(`rotate(${p.rotate}deg)`)
      if (p.skewX !== undefined) t.push(`skewX(${p.skewX}deg)`)
      if (p.skewY !== undefined) t.push(`skewY(${p.skewY}deg)`)
      if (p.scale !== undefined) t.push(`scale(${p.scale})`)
      if (p.scaleX !== undefined || p.scaleY !== undefined) t.push(`scale(${p.scaleX ?? 1},${p.scaleY ?? 1})`)
      const d: string[] = []
      if (t.length) d.push(`transform:${t.join(' ')}`)
      if (p.opacity !== undefined) d.push(`opacity:${p.opacity}`)
      if (p.blur !== undefined) d.push(`filter:blur(${r3(p.blur * u)}px)`)
      if (p.color) d.push(`color:${p.color}`)
      if (p.background) d.push(`background-color:${p.background}`)
      if (p.clip) d.push(`clip-path:inset(${p.clip.map((x) => `${x}%`).join(' ')})`)
      return d.length ? `.stage [data-el="${el}"]{${d.map((x) => `${x}!important`).join(';')}}` : ''
    })
    .filter(Boolean)
    .join('\n')
}

/** Keadaan panggung yang berlaku di beat ke-i (0-based) — untuk preview. */
export function stateAt(spec: MotionSpec | undefined, i: number, total: number): StageState {
  const st = spec?.states
  if (!st?.length) return {}
  const urut = Array.from({ length: Math.max(1, total) }, (_, k) => (k === total - 1 && total > 1 && spec!.last ? spec!.last : st[k % st.length]!))
  return lengkapi(urut)[Math.min(i, urut.length - 1)] ?? {}
}
